import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { ILeaseCustomer } from '@/types/admin/events';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { onColor } from '@/constants/shared/theme/onColor';
import { GlassCard, SectionHeader } from '@/components/shared/redesign';
import { GlassStatusBadge } from '@/components/admin/redesign';
import { SVG_COMPONENTS } from '@/assets/admin';

export const DesktopResidentDetailsPanel = ({
  resident,
  flags,
  isUpdating,
  onCheckIn,
}: {
  resident: ILeaseCustomer;
  flags: { checkedIn: boolean; hasRsvp: boolean };
  isUpdating: boolean;
  onCheckIn: (resident: ILeaseCustomer, checkedIn: boolean) => void;
}) => {
  const { redesign, semantic } = useAdminColors();

  return (
    <GlassCard padding={0} borderRadius={8} style={styles.container}>
      <View style={[styles.headerRow, { borderBottomColor: redesign.card.border }]}>
        <Text style={[styles.headerTitle, { color: redesign.text.primary }]}>
          Resident Details
        </Text>
      </View>
      <View style={styles.body}>
        <GlassCard padding={16} borderRadius={12} style={styles.infoCard}>
          <View style={styles.infoRow}>
            <View
              style={[
                styles.avatar,
                { backgroundColor: redesign.accent.blueBgLight },
              ]}
            >
              <Text style={[styles.avatarText, { color: semantic.text.link }]}>
                {`${resident.firstName.charAt(0)}${resident.lastName.charAt(0)}`.toUpperCase()}
              </Text>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[styles.name, { color: redesign.text.primary }]}>
                {resident.firstName} {resident.lastName}
              </Text>
              {!!resident.email && (
                <Text style={[styles.email, { color: redesign.text.muted }]}>
                  {resident.email}
                </Text>
              )}
              {!!resident.unitNumberSpace && (
                <Text style={[styles.unit, { color: redesign.text.muted }]}>
                  Unit {resident.unitNumberSpace}
                </Text>
              )}
              {!!resident.statusType && (
                <View
                  style={[
                    styles.statusPill,
                    { backgroundColor: redesign.glass.buttonBg },
                  ]}
                >
                  <Text
                    style={[
                      styles.statusPillText,
                      { color: redesign.text.subtle },
                    ]}
                  >
                    {resident.statusType.charAt(0).toUpperCase() +
                      resident.statusType.slice(1)}{' '}
                    resident
                  </Text>
                </View>
              )}
            </View>
          </View>
        </GlassCard>

        <GlassCard padding={16} borderRadius={12} style={styles.checkinCard}>
          <SectionHeader title="Check-in info" />
          <View style={{ marginTop: 8 }}>
            {flags.checkedIn ? (
              <GlassStatusBadge label="Checked in" variant="success" />
            ) : (
              <GlassStatusBadge label="Not checked in" variant="neutral" />
            )}
            {flags.hasRsvp && (
              <View
                style={[
                  styles.rsvpInfo,
                  { backgroundColor: redesign.accent.blueBgLight },
                ]}
              >
                <SVG_COMPONENTS.CHECK_CIRCLE
                  width={12}
                  height={12}
                  color={semantic.text.link}
                />
                <Text style={[styles.rsvpText, { color: redesign.text.primary }]}>
                  RSVP'd to this event
                </Text>
              </View>
            )}
          </View>
          <View style={{ marginTop: 16 }}>
            {!flags.checkedIn ? (
              <TouchableOpacity
                style={[
                  styles.checkInBtn,
                  { backgroundColor: semantic.status.success },
                  isUpdating && { opacity: 0.7 },
                ]}
                disabled={isUpdating}
                onPress={() => onCheckIn(resident, true)}
              >
                <Text style={[styles.checkInBtnText, { color: onColor(semantic.status.success) }]}>
                  Check in
                </Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[
                  styles.checkOutBtn,
                  {
                    backgroundColor: redesign.glass.buttonBg,
                    borderColor: redesign.card.border,
                  },
                  isUpdating && { opacity: 0.7 },
                ]}
                disabled={isUpdating}
                onPress={() => onCheckIn(resident, false)}
              >
                <Text
                  style={[styles.checkOutBtnText, { color: redesign.text.primary }]}
                >
                  Uncheck
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </GlassCard>
      </View>
    </GlassCard>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '700',
  },
  body: {
    padding: 16,
  },
  infoCard: {
    marginBottom: 12,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  avatarText: {
    fontSize: 20,
    fontWeight: '700',
  },
  name: {
    fontSize: 18,
    fontWeight: '600',
  },
  email: {
    fontSize: 14,
    marginTop: 2,
  },
  unit: {
    fontSize: 14,
    marginTop: 2,
  },
  statusPill: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    marginTop: 6,
  },
  statusPillText: {
    fontSize: 12,
    fontWeight: '500',
  },
  checkinCard: {
    // padding handled by GlassCard
  },
  rsvpInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 8,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  rsvpText: {
    fontSize: 12,
    fontWeight: '500',
  },
  checkInBtn: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignSelf: 'flex-start',
  },
  checkInBtnText: {
    // color is applied inline from onColor(status.success) — success is a
    // LIGHT green in the dark theme, where white would read at ~2:1.
    fontSize: 14,
    fontWeight: '600',
  },
  checkOutBtn: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignSelf: 'flex-start',
    borderWidth: 1,
  },
  checkOutBtnText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
